import { useContext, useEffect, useState } from 'react'
import { DataContext } from '../../../context/DataContext'
import {
  createGuestPaymentOrderRequest,
  createOneClickPaymentOrderRequest,
  finishCardSubscriptionRequest,
  subscribeCardRequest
} from '../../../helpers/pinflagRequest'

import { webpayRedirectEvent } from '../../../helpers/eventsHelpers'
import { usePayment } from '../usePayment/usePayment'
import { usePaymentOrderHandlers } from '../usePaymentOrderHandlers'
import { WEBPAY } from '../../../constants/payment'
import {
  parseCustomerData,
  parsePaymentData
} from '../../../helpers/payment/paymentOrderParsers'
import { useLocalStorage } from '../../useLocalStorage'
import { isCardNumberValid, isCvvValid, isExpiryDateValid } from '../../../helpers/payment/cardDataHelpers'

export const useWebpay = (webpayRedirect = false) => {
  const {
    ecommerceInfo,
    userInfo,
    addressInfo,
    setData,
    cartData,
    setShowFinalLoader
  } = useContext(DataContext)
  const {
    userToken,
    setPaymentState,
    selectedPaymentOption,
    setIsLoadingGatewayForm,
    installments,
    selectedPaymentCard,
    cardNumber,
    cardExpiryDate,
    cardCvv,
    isSavingCard,
    setIsPaying,
    setShowGatewayForm,
    setHasConfirmedPaymentMethod,
    setIsPayingWithPinflagMethods,
    resetCardData
  } = usePayment()
  const data = {
    userInfo,
    addressInfo,
    cartData,
    ecommerceInfo,
    selectedPaymentOption,
    installments,
    selectedPaymentCard
  }

  const [dataAndPaymentStateSetted, setDataAndPaymentStateSetted] =
    useState(false)

  const {
    saveDataContext,
    getDataContext,
    savePaymentState,
    getPaymentState,
    removeDataContext,
    removePaymentState
  } = useLocalStorage()

  const { pollPaymentOrderStatus } = usePaymentOrderHandlers()
  const handleConfirmPaymentMethodSelectionWebpay = () => {
    setIsLoadingGatewayForm(false)
  }

  const canUserPayWithWebpay = () => {
    if (isSavingCard) return true
    const cardNumberIsValid = isCardNumberValid(cardNumber)
    const expiryDateIsValid = isExpiryDateValid(cardExpiryDate)
    const cvvIsValid = isCvvValid(cardCvv)
    if (cardNumberIsValid && expiryDateIsValid && cvvIsValid) return true
    return false
  }

  const payWithWebpay = async () => {
    setShowFinalLoader(true)
    try {
      const paymentData = parsePaymentData({
        data,
        gateway: WEBPAY,
        useOneClick: false,
        forceNewPayment: false
      })
      const paymentDataWithExtraData = {
        ...paymentData,
        cardData: {
          cvv: cardCvv,
          cardNumber,
          cardExpirationDate: cardExpiryDate
        }
      }
      const paymentOrderResponse = await createGuestPaymentOrderRequest(
        paymentDataWithExtraData
      )
      setShowFinalLoader(true)
      return pollPaymentOrderStatus({
        orderId: paymentOrderResponse?.orderId,
        gateway: WEBPAY,
        paymentMethod: selectedPaymentOption
      })
    } catch (error) {
      console.error('❗Error payWithWebpay:', error)
      setIsPaying(false)
      setShowFinalLoader(false)
      setShowGatewayForm(false)
      setHasConfirmedPaymentMethod(false)
      resetCardData()
      setPaymentState((prevState) => ({
        ...prevState,
        paymentError: 'Ocurrió un error al procesar tu pago, por favor intenta nuevamente'
      }))
    }
  }

  const subscribeCardAndPayWebpay = async () => {
    try {
      const paymentData = parsePaymentData({
        data,
        gateway: WEBPAY,
        useOneClick: true,
        forceNewPayment: false
      })
      const paymentDataWithExtraData = {
        ...paymentData,
        responseUrl: ecommerceInfo.currentUrl || ecommerceInfo.domain
      }
      const subscriptionResponse = await subscribeCardRequest(
        paymentDataWithExtraData
      )
      saveDataContext()
      savePaymentState()
      webpayRedirectEvent(subscriptionResponse.redirectionUrl)
    } catch (error) {
      console.error('❗Error subscribeCardAndPayWebpay:', error)
      setIsPaying(false)
      setShowFinalLoader(false)
      setPaymentState((prevState) => ({
        ...prevState,
        paymentError: 'Ocurrió un error al procesar tu pago, por favor intenta nuevamente'
      }))
    }
  }

  const finishCardSubscriptionWebpay = async (referenceId, userInfo) => {
    return finishCardSubscriptionRequest({
      paymentGateway: WEBPAY,
      referenceId,
      customerData: parseCustomerData(userInfo)
    })
  }

  const handleOneClickPaymentWebpay = async (data, userToken) => {
    try {
      const paymentData = parsePaymentData({
        data,
        gateway: WEBPAY,
        useOneClick: true,
        forceNewPayment: false
      })
      const card = data.selectedPaymentCard
      const paymentOrderResponse = await createOneClickPaymentOrderRequest(
        paymentData,
        userToken
      )
      return pollPaymentOrderStatus({
        orderId: paymentOrderResponse?.orderId,
        gateway: WEBPAY,
        paymentMethod: card?.cardType
          ? card?.cardType?.toLowerCase()
          : data.selectedPaymentOption
      })
    } catch (error) {
      setIsPaying(false)
      setShowFinalLoader(false)
      setPaymentState((prevState) => ({
        ...prevState,
        paymentError: 'Ocurrió un error al procesar tu pago, por favor intenta nuevamente'
      }))
    }
  }

  const payOneClickWithWebpay = async () => {
    return handleOneClickPaymentWebpay(data, userToken.current)
  }

  const confirmPaymentMethodWithWebpay = () => {
    // Show gateway form
    setShowGatewayForm(true)
    setIsLoadingGatewayForm(false)
  }

  useEffect(() => {
    if (!webpayRedirect) return
    const dataContext = getDataContext()
    const paymentState = getPaymentState()
    setData(dataContext)
    setPaymentState(paymentState)
    setShowFinalLoader(true)
    setIsPayingWithPinflagMethods(true)
  }, [webpayRedirect])

  useEffect(() => {
    if (!webpayRedirect) return
    const dataContext = getDataContext()
    const paymentState = getPaymentState()
    setData(dataContext)
    setPaymentState(paymentState)
    removeDataContext()
    removePaymentState()
    // Trigger the payment process
    setDataAndPaymentStateSetted(true)
    setIsPayingWithPinflagMethods(true)
    setShowFinalLoader(true)
  }, [webpayRedirect])

  useEffect(() => {
    if (!webpayRedirect || !dataAndPaymentStateSetted) return

    const processPayment = async () => {
      try {
        const response = await finishCardSubscriptionWebpay(
          data.cartData.referenceId,
          data.userInfo
        )
        const oneClickData = {
          userInfo: data.userInfo,
          addressInfo: data.addressInfo,
          cartData: data.cartData,
          ecommerceInfo: data.ecommerceInfo,
          selectedPaymentOption: data.selectedPaymentOption,
          installments: data.installments,
          selectedPaymentCard: response.cardData
        }

        await handleOneClickPaymentWebpay(oneClickData, response.userToken)
      } catch (error) {
        console.error('❗Error finishCardSubscriptionWebpay:', error)
        setIsPaying(false)
        setShowFinalLoader(false)
        setPaymentState((prevState) => ({
          ...prevState,
          paymentError: 'Ocurrió un error al procesar tu pago, por favor intenta nuevamente'
        }))
      }
    }
    processPayment()
  }, [webpayRedirect, dataAndPaymentStateSetted])

  return {
    payWithWebpay,
    payOneClickWithWebpay,
    subscribeCardAndPayWebpay,
    confirmPaymentMethodWithWebpay,
    handleConfirmPaymentMethodSelectionWebpay,
    canUserPayWithWebpay
  }
}
